/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input, OnChanges, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DateInputComponent, PageHeaderComponent, PageStateComponent } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { FormAddressComponent } from './form-address.component';
import { FormPdfDialogComponent } from './form-pdf-dialog.component';
import { FormSignatureComponent } from './form-signature.component';
import {
  bahtText,
  columnTotal,
  emptyRow,
  emptyValues,
  FormColumn,
  FormDefinition,
  FormField,
  FormProblemCode,
  FormSection,
  FormState,
  FormValues,
  isTotalled,
  isNumericColumn,
  isWideType,
  maxRows,
  pdfName,
  prepare,
  printedType,
  toNumber,
  validateAll,
  validateValue
} from './forms.model';
import { FlowProblem, FormPrintError, FormsService, PrintedForm, SubmittedForm } from './forms.service';

type Row = Record<string, string | number>;

/** The approval flow a filed form starts: under way, started (with its first process), or why not. */
interface FlowState {
  state: 'starting' | 'started' | FlowProblem;
  processId?: string;
  /** False when the step chain could not be kept, so only its first step will run. */
  recorded?: boolean;
}

/**
 * Filling in a form. Each field is checked as the person leaves it and all of
 * them on submit; the jobs service checks again and prints the PDF, which is
 * then stored as the person in the folder the form names.
 *
 * The builder shows the same component as its preview, with `preview` on:
 * nothing is stored, and the PDF is printed from the unsaved definition.
 */
@Component({
  selector: 'app-form-fill',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatProgressBarModule,
    DateInputComponent,
    FormAddressComponent,
    FormSignatureComponent,
    PageHeaderComponent,
    PageStateComponent,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './form-fill.component.html',
  styleUrls: ['./form-fill.component.scss']
})
export class FormFillComponent implements OnInit, OnChanges {
  private readonly forms = inject(FormsService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Given by the builder; otherwise the form is loaded from the route. */
  @Input() definition?: FormDefinition;
  @Input() preview = false;

  readonly form = signal<FormDefinition | null>(null);
  readonly loading = signal(false);
  readonly failed = signal('');
  readonly working = signal<'pdf' | 'submit' | null>(null);
  readonly problems = signal<Record<string, FormProblemCode>>({});
  readonly error = signal('');
  readonly done = signal<SubmittedForm | null>(null);
  readonly flow = signal<FlowState | null>(null);
  /** What was filed, for starting its flow again. */
  private filedValues: FormValues = {};

  values: FormValues = {};
  /** What the form's rules make of the values: hidden fields, worked-out sums. Refreshed on every change. */
  state: FormState = { values: {}, hidden: new Set(), hiddenSections: new Set() };
  /** The rows a table takes: on a paper form, as many as the paper has lines for. */
  rowLimit(field: FormField): number {
    const form = this.form();
    return form ? maxRows(form, field) : 0;
  }

  ngOnInit(): void {
    if (this.definition) {
      return;
    }
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      return;
    }
    this.loading.set(true);
    this.forms.get(id).subscribe({
      next: (form) => {
        this.loading.set(false);
        if (form.status !== 'published') {
          this.failed.set(this.translate.instant('DMS.FORMS.NOT_PUBLISHED'));
          return;
        }
        this.start(form);
      },
      error: (error) => {
        this.loading.set(false);
        this.failed.set(error?.message ?? String(error));
      }
    });
  }

  ngOnChanges(): void {
    if (this.definition) {
      const before = this.values;
      this.form.set(this.definition);
      // the builder changes the form under a preview someone is typing into: keep what still fits
      const fresh = emptyValues(this.definition);
      this.values = Object.fromEntries(Object.entries(fresh).map(([key, blank]) => [key, key in before ? before[key] : blank]));
      this.problems.set({});
      this.refresh();
    }
  }

  start(form: FormDefinition): void {
    this.form.set(form);
    this.values = emptyValues(form);
    this.problems.set({});
    this.error.set('');
    this.done.set(null);
    this.flow.set(null);
    this.refresh();
  }

  /** Re-runs the form's rules: which fields show, and what the sums come to. A field that goes out of sight takes its problem with it. */
  refresh(): void {
    const form = this.form();
    if (!form) {
      return;
    }
    this.state = prepare(form, this.values);
    const problems = this.problems();
    if (Object.keys(problems).some((key) => this.state.hidden.has(key))) {
      this.problems.set(Object.fromEntries(Object.entries(problems).filter(([key]) => !this.state.hidden.has(key))));
    }
  }

  shown(field: FormField): boolean {
    return !this.state.hidden.has(field.key);
  }

  /** A section shows while its condition holds and something in it does. */
  sectionShown(section: FormSection): boolean {
    return !this.state.hiddenSections.has(section) && section.fields.some((f) => this.shown(f));
  }

  // ── fields ────────────────────────────────────────────────────────────

  wide(field: FormField, columns: number): boolean {
    return columns < 2 || field.width !== 'half' || isWideType(field.type);
  }

  /** Re-checks one field once the person has moved on from it, or as they fix one already marked. */
  check(field: FormField): void {
    this.refresh();
    const form = this.form();
    const problem = validateValue(field, this.values[field.key], form ? maxRows(form, field) : undefined);
    this.problems.update((all) => {
      const next = { ...all };
      if (problem) {
        next[field.key] = problem;
      } else {
        delete next[field.key];
      }
      return next;
    });
  }

  changed(field: FormField): void {
    this.refresh();
    if (this.problems()[field.key]) {
      this.check(field);
    }
  }

  problemText(field: FormField): string {
    const code = this.problems()[field.key];
    if (code === 'required' && field.type === 'checkbox') {
      return this.translate.instant('DMS.FORMS.PROBLEM_TICK');
    }
    return code ? this.translate.instant(`DMS.FORMS.PROBLEM_${code.toUpperCase()}`) : '';
  }

  inputType(field: FormField): string {
    return field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text';
  }

  inputMode(field: FormField): string | null {
    return field.type === 'number' || field.type === 'money' ? 'decimal' : field.type === 'thaiId' ? 'numeric' : null;
  }

  /** A calc field's answer, worked out as the service will; blank until it can be. */
  calcValue(field: FormField): number | null {
    const value = this.state.values[field.key];
    return typeof value === 'number' ? value : null;
  }

  printedType(field: FormField | FormColumn): string {
    return printedType(field);
  }

  amountWords(value: unknown): string {
    const amount = toNumber(value as string);
    return amount === null ? '' : bahtText(amount);
  }

  isChecked(field: FormField, option: string): boolean {
    return ((this.values[field.key] as string[]) ?? []).includes(option);
  }

  toggle(field: FormField, option: string, on: boolean): void {
    const chosen = new Set((this.values[field.key] as string[]) ?? []);
    if (on) {
      chosen.add(option);
    } else {
      chosen.delete(option);
    }
    // keep the order the form lists them in, as the PDF does
    this.values[field.key] = (field.options ?? []).filter((o) => chosen.has(o));
    this.changed(field);
  }

  rows(field: FormField): Row[] {
    return (this.values[field.key] as Row[]) ?? [];
  }

  addRow(field: FormField): void {
    if (this.rows(field).length < this.rowLimit(field)) {
      this.values[field.key] = [...this.rows(field), emptyRow(field)];
      this.refresh();
    }
  }

  removeRow(field: FormField, index: number): void {
    const rows = this.rows(field).filter((_, i) => i !== index);
    this.values[field.key] = rows.length ? rows : [emptyRow(field)];
    this.changed(field);
  }

  hasMoney(field: FormField): boolean {
    return (field.columns ?? []).some((c) => isTotalled(field, c));
  }

  totalled(field: FormField, column: FormColumn): boolean {
    return isTotalled(field, column);
  }

  /** Totals add up the rows as worked out, calc columns included. */
  total(field: FormField, column: FormColumn): number {
    return columnTotal((this.state.values[field.key] as Row[] | undefined) ?? [], column.key);
  }

  /** A calc cell's answer; blank in a row with nothing typed in it yet. */
  calcCell(field: FormField, index: number, column: FormColumn): number | null {
    const value = ((this.state.values[field.key] as Row[] | undefined) ?? [])[index]?.[column.key];
    return typeof value === 'number' ? value : null;
  }

  numeric(column: FormColumn): boolean {
    return isNumericColumn(column);
  }

  // ── PDF and submitting ────────────────────────────────────────────────

  showPdf(): void {
    const form = this.form();
    if (!form || this.working()) {
      return;
    }
    this.error.set('');
    this.working.set('pdf');
    // only what the form asks goes out; the service works the sums out again itself
    const values = prepare(form, this.values).values;
    const print: Observable<PrintedForm> = this.preview ? this.forms.preview(form, values) : this.forms.print(form.id ?? '', values);
    print.subscribe({
      next: ({ pdf, reference }) => {
        this.working.set(null);
        this.dialog.open(FormPdfDialogComponent, {
          data: { pdf, fileName: pdfName(form, reference || 'preview') },
          width: '920px',
          maxWidth: '96vw',
          height: '90vh',
          panelClass: 'aca-frmpdf-panel'
        });
      },
      error: (reason: FormPrintError) => this.failedToPrint(reason)
    });
  }

  submit(): void {
    const form = this.form();
    if (!form || this.preview || this.working()) {
      return;
    }
    const problems = validateAll(form, this.values);
    this.problems.set(problems);
    if (Object.keys(problems).length) {
      this.error.set(this.translate.instant('DMS.FORMS.FIX_FIELDS', { count: Object.keys(problems).length }));
      this.focusFirstProblem();
      return;
    }
    this.error.set('');
    this.working.set('submit');
    const values = prepare(form, this.values).values;
    this.forms.submit(form, values).subscribe({
      next: (done) => {
        this.working.set(null);
        this.done.set(done);
        this.filedValues = values;
        this.startFlow();
      },
      error: (reason: FormPrintError) => this.failedToPrint(reason)
    });
  }

  /** Starts the form's approval flow on the PDF just filed — again, when the first try failed. */
  startFlow(): void {
    const form = this.form();
    const filed = this.done();
    if (!form?.output?.workflowId || !filed || this.flow()?.state === 'starting') {
      return;
    }
    this.flow.set({ state: 'starting' });
    this.forms.startWorkflow(form, filed, this.filedValues).subscribe({
      next: ({ process, recorded }) => this.flow.set({ state: 'started', processId: process.id, recorded }),
      error: (problem: FlowProblem) => this.flow.set({ state: problem })
    });
  }

  again(): void {
    const form = this.form();
    if (form) {
      this.start(form);
    }
  }

  private failedToPrint(reason: FormPrintError): void {
    this.working.set(null);
    if (reason?.kind === 'values' && reason.problems) {
      this.problems.set(reason.problems);
      this.error.set(this.translate.instant('DMS.FORMS.FIX_FIELDS', { count: Object.keys(reason.problems).length }));
      this.focusFirstProblem();
    } else if (reason?.kind === 'engine') {
      this.error.set(this.translate.instant('DMS.FORMS.ENGINE_DOWN'));
    } else {
      this.error.set(this.translate.instant('DMS.FORMS.SUBMIT_FAILED', { message: (reason as FormPrintError)?.message ?? '' }));
    }
  }

  private focusFirstProblem(): void {
    setTimeout(() => {
      const first = this.host.nativeElement.querySelector('.aca-frmfill__field--bad');
      first?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      (first?.querySelector('input, select, textarea') as HTMLElement | null)?.focus({ preventScroll: true });
    });
  }
}
